import type { Store } from "@components/modules/projects/list/Store/Store";
import { t } from "@lingui/macro";
import {
  BulkStarProjectsDocument,
  type BulkStarProjectsMutation,
  type BulkStarProjectsMutationVariables,
  BulkUnstarProjectsDocument,
  type BulkUnstarProjectsMutation,
  type BulkUnstarProjectsMutationVariables,
  StarProjectDocument,
  type StarProjectMutation,
  type StarProjectMutationVariables,
  UnstarProjectDocument,
  type UnstarProjectMutation,
  type UnstarProjectMutationVariables,
} from "@src/__generated__/graphql";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import { DisclosureState } from "@src/utils/mobx/states/DisclosureState";
import { QueueState } from "@src/utils/mobx/states/queue-state";
import { action, computed, makeObservable, observable } from "mobx";

type StarProjectModalActionType = "star" | "unstar" | "bulkStar" | "bulkUnstar";

type StarProjectModalOptions = {
  ids: string | string[];
  action: StarProjectModalActionType;
};

export class StarProjectModalStore {
  private parentStore: Store;

  @observable.ref
  public withMyTasks: boolean = false;

  private queue: QueueState<string>;

  private starProjectMutator = new MutationHelper<
    StarProjectMutation,
    StarProjectMutationVariables
  >(StarProjectDocument);

  private unstarProjectMutator = new MutationHelper<
    UnstarProjectMutation,
    UnstarProjectMutationVariables
  >(UnstarProjectDocument);

  private bulkStarProjectsMutator = new MutationHelper<
    BulkStarProjectsMutation,
    BulkStarProjectsMutationVariables
  >(BulkStarProjectsDocument);

  private bulkUnstarProjectsMutator = new MutationHelper<
    BulkUnstarProjectsMutation,
    BulkUnstarProjectsMutationVariables
  >(BulkUnstarProjectsDocument);

  @observable
  modalState = new DisclosureState<StarProjectModalOptions>();

  constructor(parentStore: Store) {
    makeObservable(this);
    this.parentStore = parentStore;
    this.queue = new QueueState();
  }

  @computed
  get isLoading() {
    return (id: string) =>
      this.queue.isQueued(id) &&
      (this.starProjectMutator.isLoading.value ||
        this.unstarProjectMutator.isLoading.value);
  }

  @computed
  get isAnyLoading() {
    return (
      this.starProjectMutator.isLoading.value ||
      this.unstarProjectMutator.isLoading.value ||
      this.bulkStarProjectsMutator.isLoading.value ||
      this.bulkUnstarProjectsMutator.isLoading.value
    );
  }

  @computed
  get isBulkLoading() {
    return (
      this.bulkStarProjectsMutator.isLoading.value ||
      this.bulkUnstarProjectsMutator.isLoading.value
    );
  }

  @computed
  get isActionStar() {
    return ["star", "bulkStar"].includes(
      this.modalState.additionalData?.action!,
    );
  }

  @computed
  get isActionUnstar() {
    return ["unstar", "bulkUnstar"].includes(
      this.modalState.additionalData?.action!,
    );
  }

  @action.bound
  public handleWithMyTasks(star: boolean) {
    this.withMyTasks = star;
  }

  @action.bound
  public async confirm() {
    const action = this.modalState.additionalData?.action;
    const ids = this.modalState.additionalData?.ids ?? [];

    if (!action || ids.length === 0) return;

    switch (action) {
      case "star":
        await this.starProject(ids as string);
        return;
      case "unstar":
        await this.unstarProject(ids as string);
        return;
      case "bulkStar":
        await this.bulkStarProjects(ids as string[]);
        return;
      case "bulkUnstar":
        await this.bulkUnstarProjects(ids as string[]);
        return;
    }
  }

  @action.bound
  private async starProject(id: string) {
    this.queue.enqueue(id);

    const [_, error] = await this.starProjectMutator.mutate({
      project_id: id,
      options: {
        with_my_tasks: this.withMyTasks,
      },
    });

    this.queue.dequeue(id);

    if (error) return;

    this.updateStarredProjects([id], true);

    this.parentStore.appStore.UIStore.toast({
      title: this.withMyTasks
        ? t`Project starred and assigned tasks to you.`
        : t`Project starred.`,
      status: "success",
    });

    this.modalState.onClose();
  }

  @action.bound
  private async unstarProject(id: string): Promise<void> {
    this.queue.enqueue(id);

    const [_, error] = await this.unstarProjectMutator.mutate({
      project_id: id,
      options: {
        with_my_tasks: this.withMyTasks,
      },
    });

    this.queue.dequeue(id);

    if (error) return;

    this.updateStarredProjects([id], false);

    this.parentStore.appStore.UIStore.toast({
      title: this.withMyTasks
        ? t`Project unstarred and assigned tasks to you.`
        : t`Project unstarred.`,
      status: "success",
    });

    this.modalState.onClose();
  }

  @action.bound
  private async bulkStarProjects(ids: string[]) {
    this.queue.enqueue(ids);

    const [data, error] = await this.bulkStarProjectsMutator.mutate({
      project_ids: ids,
      options: {
        with_my_tasks: this.withMyTasks,
      },
    });

    this.queue.dequeue(ids);

    if (error) return;

    this.updateStarredProjects(ids, true);

    this.parentStore.appStore.UIStore.toast({
      title: this.withMyTasks
        ? t`Starred ${data.bulkStarProjects.total - data.bulkStarProjects.failed} projects out of ${data.bulkStarProjects.total} and assigned tasks to you.`
        : t`Starred ${data.bulkStarProjects.total - data.bulkStarProjects.failed} projects out of ${data.bulkStarProjects.total}.`,
      status: "success",
    });

    this.parentStore.setSelectedProjectIds([]);
    this.modalState.onClose();
  }

  @action.bound
  private async bulkUnstarProjects(ids: string[]): Promise<void> {
    this.queue.enqueue(ids);

    const [data, error] = await this.bulkUnstarProjectsMutator.mutate({
      project_ids: ids,
      options: {
        with_my_tasks: this.withMyTasks,
      },
    });

    this.queue.dequeue(ids);

    if (error) return;

    this.updateStarredProjects(ids, false);

    this.parentStore.appStore.UIStore.toast({
      title: this.withMyTasks
        ? t`Unstarred ${data.bulkUnstarProjects.total - data.bulkUnstarProjects.failed} projects out of ${data.bulkUnstarProjects.total} and assigned tasks to you.`
        : t`Unstarred ${data.bulkUnstarProjects.total - data.bulkUnstarProjects.failed} projects out of ${data.bulkUnstarProjects.total}.`,
      status: "success",
    });

    this.parentStore.setSelectedProjectIds([]);
    this.modalState.onClose();
  }

  @action.bound
  private updateStarredProjects(ids: string[], isStarred: boolean) {
    this.parentStore.projects = this.parentStore.projects.map((project) => {
      if (ids.includes(project.id)) {
        return Object.assign(project, { is_starred: isStarred });
      }
      return project;
    });
  }
}
